import React, { useState } from 'react';
import { useStore, notifyBothWhatsApp, notifyWhatsApp } from '../context/ProductStore';

const PAYMENT_METHODS = [
  { id: 'ecocash',  label: 'EcoCash',           icon: '📱', color: '#16a34a', description: 'Pay via EcoCash mobile money' },
  { id: 'swipe',    label: 'Swipe / Card',       icon: '💳', color: '#2563eb', description: 'Pay by debit or credit card at pickup' },
  { id: 'transfer', label: 'Online Transfer',    icon: '🏦', color: '#7c3aed', description: 'Bank transfer or ZIPIT' },
];

const CheckoutModal = ({ onClose }) => {
  const { cartItems, cartTotal, placeOrder } = useStore();

  const [step,        setStep]        = useState(1);
  const [payMethod,   setPayMethod]   = useState('ecocash');
  const [details,     setDetails]     = useState({ name: '', phone: '', address: '' });
  const [ecocashNum,  setEcocashNum]  = useState('');
  const [processing,  setProcessing]  = useState(false);
  const [orderDone,   setOrderDone]   = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [notifyState, setNotifyState] = useState('idle'); // idle | sending | sent

  // ── Place order + send WhatsApp to both numbers ───────────────
  const handleOrder = async () => {
    setProcessing(true);

    const orderItems = cartItems.map(i => ({
      name:      i.product.name,
      qty:       i.qty,
      unitPrice: i.product.isPromo ? i.product.promoPrice : i.product.price,
    }));

    const order = placeOrder({
      customerName:   details.name,
      customerPhone:  details.phone,
      address:        details.address,
      paymentMethod:  payMethod,
      ecocashNumber:  ecocashNum,
      items:          orderItems,
      total:          cartTotal,
    });

    // Simulate brief processing delay
    await new Promise(r => setTimeout(r, 1200));

    setPlacedOrder(order);
    setProcessing(false);
    setOrderDone(true);

    // Automatically open WhatsApp for both numbers
    setNotifyState('sending');
    setTimeout(() => {
      notifyBothWhatsApp(order);
      setNotifyState('sent');
    }, 600);
  };

  // ── Success Screen ────────────────────────────────────────────
  if (orderDone && placedOrder) {
    const payLabel = PAYMENT_METHODS.find(p => p.id === payMethod)?.label;

    return (
      <div style={S.overlay}>
        <div style={S.modal}>
          <div style={S.successScreen}>
            <div style={S.successAnim}>✅</div>
            <h2 style={S.successTitle}>Order Placed!</h2>
            <p style={S.orderId}>Order #{placedOrder.id}</p>

            <p style={S.successMsg}>
              Thank you, <strong style={{ color: '#CC0000' }}>{details.name}</strong>!
              Your order has been received. Our team will contact you on{' '}
              <strong style={{ color: '#CC0000' }}>{details.phone}</strong> to confirm
              payment and delivery.
            </p>

            {/* Order summary */}
            <div style={S.summaryBox}>
              <div style={S.summaryRow}>
                <span>Order ID</span>
                <span style={{ fontFamily: 'monospace', color: '#CC0000' }}>#{placedOrder.id}</span>
              </div>
              <div style={S.summaryRow}>
                <span>Total</span>
                <span style={{ color: '#CC0000', fontWeight: '800' }}>USD {placedOrder.total.toFixed(2)}</span>
              </div>
              <div style={S.summaryRow}>
                <span>Payment</span>
                <span>{payLabel}</span>
              </div>
            </div>

            {/* WhatsApp notification status */}
            <div style={S.waNotifyBox}>
              <div style={S.waNotifyTitle}>
                {notifyState === 'sent' ? '✅ Team Notified on WhatsApp!' : '📨 Notifying your team…'}
              </div>
              <div style={S.waNumbers}>
                <span style={S.waNum}>📱 +263 772 758 875</span>
                <span style={S.waNum}>📱 +263 785 661 758</span>
              </div>
              {notifyState === 'sent' && (
                <p style={S.waHint}>
                  WhatsApp opened for both numbers with full order details.
                  If it didn't open automatically, use the buttons below.
                </p>
              )}
            </div>

            {/* Manual resend buttons */}
            <div style={S.manualBtns}>
              <button onClick={() => notifyWhatsApp(placedOrder, 0)} style={S.waBtn('#16a34a')}>
                📱 Resend to +263 772 758 875
              </button>
              <button onClick={() => notifyWhatsApp(placedOrder, 1)} style={S.waBtn('#16a34a')}>
                📱 Resend to +263 785 661 758
              </button>
            </div>

            <button onClick={onClose} style={S.doneBtn}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main Checkout ─────────────────────────────────────────────
  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        {/* Header with steps */}
        <div style={S.header}>
          <div>
            <h2 style={S.title}>🛒 Checkout</h2>
            <div style={S.steps}>
              {['Details', 'Payment', 'Confirm'].map((s, i) => (
                <React.Fragment key={s}>
                  <div style={S.stepDot(step > i)}>
                    <span>{step > i + 1 ? '✓' : i + 1}</span>
                  </div>
                  <span style={S.stepLabel(step === i + 1)}>{s}</span>
                  {i < 2 && <div style={S.stepLine(step > i + 1)} />}
                </React.Fragment>
              ))}
            </div>
          </div>
          <button onClick={onClose} style={S.closeBtn}>✕</button>
        </div>

        <div style={S.body}>

          {/* ── STEP 1: CUSTOMER DETAILS ── */}
          {step === 1 && (
            <div style={S.stepContent}>
              <h3 style={S.stepTitle}>Your Details</h3>

              {[
                { key: 'name',    label: 'Full Name *',                    placeholder: 'Your full name',          type: 'text' },
                { key: 'phone',   label: 'Phone Number *',                 placeholder: '+263 7XX XXX XXX',        type: 'tel'  },
                { key: 'address', label: 'Delivery Address (optional)',     placeholder: 'Or collect at Eastgate Mall, Harare', type: 'text' },
              ].map(f => (
                <div key={f.key} style={S.field}>
                  <label style={S.label}>{f.label}</label>
                  <input
                    style={S.input}
                    type={f.type}
                    placeholder={f.placeholder}
                    value={details[f.key]}
                    onChange={e => setDetails(d => ({ ...d, [f.key]: e.target.value }))}
                  />
                </div>
              ))}

              {/* Mini cart */}
              <div style={S.miniCart}>
                <div style={S.miniCartTitle}>Order Summary ({cartItems.length} item{cartItems.length !== 1 ? 's' : ''})</div>
                {cartItems.map(item => {
                  const price = item.product.isPromo ? item.product.promoPrice : item.product.price;
                  return (
                    <div key={item.productId} style={S.miniCartRow}>
                      <span style={{ flex: 1 }}>{item.product.name} × {item.qty}</span>
                      <span>USD {(price * item.qty).toFixed(2)}</span>
                    </div>
                  );
                })}
                <div style={S.miniCartTotal}>
                  <span>Total</span>
                  <span style={{ color: '#CC0000' }}>USD {cartTotal.toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={() => {
                  if (!details.name.trim() || !details.phone.trim())
                    return alert('Please enter your name and phone number.');
                  setStep(2);
                }}
                style={S.nextBtn}
              >
                Next: Choose Payment →
              </button>
            </div>
          )}

          {/* ── STEP 2: PAYMENT ── */}
          {step === 2 && (
            <div style={S.stepContent}>
              <h3 style={S.stepTitle}>Payment Method</h3>

              <div style={S.payMethods}>
                {PAYMENT_METHODS.map(pm => (
                  <button
                    key={pm.id}
                    onClick={() => setPayMethod(pm.id)}
                    style={S.payMethod(payMethod === pm.id, pm.color)}
                  >
                    <span style={S.payIcon}>{pm.icon}</span>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={S.payLabel}>{pm.label}</div>
                      <div style={S.payDesc}>{pm.description}</div>
                    </div>
                    {payMethod === pm.id && <span style={S.payCheck}>✓</span>}
                  </button>
                ))}
              </div>

              {/* EcoCash details */}
              {payMethod === 'ecocash' && (
                <div style={S.instrBox('#16a34a')}>
                  <div style={S.instrTitle}>📱 EcoCash Payment Instructions</div>
                  <p style={S.instrText}>
                    After placing your order, send <strong style={{ color: '#4ade80' }}>USD {cartTotal.toFixed(2)}</strong> to either number:
                  </p>
                  <div style={S.instrNumbers}>
                    <div style={S.instrNum}>0772 758 875 — Techdave Solutions</div>
                    <div style={S.instrNum}>0785 661 758 — Techdave Solutions</div>
                  </div>
                  <input
                    style={S.input}
                    placeholder="Your EcoCash number (optional)"
                    value={ecocashNum}
                    onChange={e => setEcocashNum(e.target.value)}
                  />
                </div>
              )}

              {/* Swipe details */}
              {payMethod === 'swipe' && (
                <div style={S.instrBox('#2563eb')}>
                  <div style={S.instrTitle}>💳 Card / Swipe at Pickup</div>
                  <p style={S.instrText}>
                    Pay <strong style={{ color: '#93c5fd' }}>USD {cartTotal.toFixed(2)}</strong> by card when you collect your order:
                  </p>
                  <div style={S.instrNumbers}>
                    <div style={S.instrNum}>📍 B30 Eastgate Mall, Harare</div>
                    <div style={S.instrNum}>🕐 Mon – Sat: 8:00AM – 6:00PM</div>
                  </div>
                </div>
              )}

              {/* Transfer details */}
              {payMethod === 'transfer' && (
                <div style={S.instrBox('#7c3aed')}>
                  <div style={S.instrTitle}>🏦 Bank Transfer / ZIPIT</div>
                  <p style={S.instrText}>
                    Transfer <strong style={{ color: '#c4b5fd' }}>USD {cartTotal.toFixed(2)}</strong> to:
                  </p>
                  <div style={S.instrNumbers}>
                    <div style={S.instrNum}>Account Name: Techdave Solutions</div>
                    <div style={S.instrNum}>Bank: CBZ Bank</div>
                  </div>
                  <p style={S.instrNote}>
                    Send proof of payment via WhatsApp to +263 772 758 875 or +263 785 661 758
                  </p>
                </div>
              )}

              <div style={S.navBtns}>
                <button onClick={() => setStep(1)} style={S.backBtn}>← Back</button>
                <button onClick={() => setStep(3)} style={S.nextBtn}>Review Order →</button>
              </div>
            </div>
          )}

          {/* ── STEP 3: CONFIRM ── */}
          {step === 3 && (
            <div style={S.stepContent}>
              <h3 style={S.stepTitle}>Confirm Your Order</h3>

              <div style={S.confirmCard}>
                <div style={S.confirmSection}>
                  <div style={S.confirmSectionTitle}>📋 Your Details</div>
                  <div style={S.confirmRow}><span>Name</span><strong>{details.name}</strong></div>
                  <div style={S.confirmRow}><span>Phone</span><strong>{details.phone}</strong></div>
                  {details.address && <div style={S.confirmRow}><span>Address</span><strong>{details.address}</strong></div>}
                </div>

                <div style={S.confirmSection}>
                  <div style={S.confirmSectionTitle}>💳 Payment Method</div>
                  <div style={S.confirmRow}>
                    <span>Method</span>
                    <strong>{PAYMENT_METHODS.find(p => p.id === payMethod)?.label}</strong>
                  </div>
                  {ecocashNum && <div style={S.confirmRow}><span>EcoCash No.</span><strong>{ecocashNum}</strong></div>}
                </div>

                <div style={S.confirmSection}>
                  <div style={S.confirmSectionTitle}>🛍️ Items Ordered</div>
                  {cartItems.map(item => {
                    const price = item.product.isPromo ? item.product.promoPrice : item.product.price;
                    return (
                      <div key={item.productId} style={S.confirmRow}>
                        <span>{item.product.name} × {item.qty}</span>
                        <span>USD {(price * item.qty).toFixed(2)}</span>
                      </div>
                    );
                  })}
                  <div style={S.confirmTotal}>
                    <span>TOTAL</span>
                    <span style={{ color: '#CC0000' }}>USD {cartTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* WhatsApp notify info */}
              <div style={S.notifyInfo}>
                <span style={{ fontSize: '20px' }}>📲</span>
                <div>
                  <div style={{ fontWeight: '700', color: '#fff', fontSize: '13px' }}>
                    Your team will be notified on WhatsApp
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
                    +263 772 758 875 · +263 785 661 758
                  </div>
                </div>
              </div>

              <div style={S.navBtns}>
                <button onClick={() => setStep(2)} style={S.backBtn}>← Back</button>
                <button onClick={handleOrder} style={S.placeBtn} disabled={processing}>
                  {processing
                    ? <span>⏳ Processing…</span>
                    : <span>✅ Place Order & Notify Team</span>
                  }
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────
const S = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9998,
    backgroundColor: 'rgba(0,0,0,0.82)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '16px', backdropFilter: 'blur(5px)',
  },
  modal: {
    backgroundColor: '#1a1a1a', borderRadius: '20px',
    width: '100%', maxWidth: '580px', maxHeight: '92vh',
    overflowY: 'auto', border: '1px solid rgba(204,0,0,0.25)',
    boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '24px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  title: { fontSize: '20px', fontWeight: '800', color: '#fff', margin: '0 0 14px' },
  steps: { display: 'flex', alignItems: 'center', gap: '6px' },
  stepDot: (done) => ({
    width: '24px', height: '24px', borderRadius: '50%',
    backgroundColor: done ? '#CC0000' : 'rgba(255,255,255,0.12)',
    color: '#fff', fontSize: '11px', fontWeight: '800',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }),
  stepLabel: (active) => ({
    fontSize: '12px', fontWeight: active ? '700' : '500',
    color: active ? '#fff' : 'rgba(255,255,255,0.35)',
  }),
  stepLine: (done) => ({
    flex: 1, height: '2px', minWidth: '24px',
    backgroundColor: done ? '#CC0000' : 'rgba(255,255,255,0.1)', borderRadius: '1px',
  }),
  closeBtn: {
    background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff',
    width: '34px', height: '34px', borderRadius: '50%', cursor: 'pointer', fontSize: '15px',
  },
  body: {},
  stepContent: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' },
  stepTitle: { fontSize: '18px', fontWeight: '800', color: '#fff', margin: 0 },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '12px', fontWeight: '700', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em' },
  input: {
    backgroundColor: '#242424', border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: '10px', padding: '12px 14px', color: '#fff',
    fontSize: '14px', fontFamily: 'inherit', outline: 'none', width: '100%',
  },
  miniCart: {
    backgroundColor: '#242424', borderRadius: '12px', padding: '16px',
    display: 'flex', flexDirection: 'column', gap: '8px',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  miniCartTitle: { fontSize: '12px', fontWeight: '700', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' },
  miniCartRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'rgba(255,255,255,0.8)' },
  miniCartTotal: {
    display: 'flex', justifyContent: 'space-between', fontSize: '17px', fontWeight: '800',
    borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '10px', marginTop: '4px', color: '#fff',
  },
  payMethods: { display: 'flex', flexDirection: 'column', gap: '10px' },
  payMethod: (active, color) => ({
    display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px',
    borderRadius: '12px', border: `2px solid ${active ? color : 'rgba(255,255,255,0.08)'}`,
    background: active ? `${color}18` : '#242424',
    cursor: 'pointer', transition: 'all 0.2s',
  }),
  payIcon: { fontSize: '28px', flexShrink: 0 },
  payLabel: { fontSize: '15px', fontWeight: '700', color: '#fff' },
  payDesc: { fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginTop: '2px' },
  payCheck: { marginLeft: 'auto', color: '#4ade80', fontSize: '20px', fontWeight: '800' },
  instrBox: (color) => ({
    backgroundColor: `${color}12`, border: `1px solid ${color}45`,
    borderRadius: '12px', padding: '16px',
    display: 'flex', flexDirection: 'column', gap: '10px',
  }),
  instrTitle: { fontSize: '14px', fontWeight: '800', color: '#fff' },
  instrText: { fontSize: '13px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, margin: 0 },
  instrNumbers: { display: 'flex', flexDirection: 'column', gap: '6px' },
  instrNum: { fontSize: '15px', fontWeight: '700', color: '#fff', letterSpacing: '0.02em' },
  instrNote: { fontSize: '12px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', margin: 0 },
  navBtns: { display: 'flex', gap: '12px' },
  backBtn: {
    padding: '13px 20px', borderRadius: '10px',
    border: '1.5px solid rgba(255,255,255,0.15)',
    background: 'transparent', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '14px',
  },
  nextBtn: {
    flex: 1, padding: '13px 20px', borderRadius: '10px', border: 'none',
    background: 'linear-gradient(135deg,#CC0000,#990000)',
    color: '#fff', fontWeight: '700', fontSize: '15px', cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(204,0,0,0.3)',
  },
  placeBtn: {
    flex: 1, padding: '13px 20px', borderRadius: '10px', border: 'none',
    background: 'linear-gradient(135deg,#16a34a,#15803d)',
    color: '#fff', fontWeight: '700', fontSize: '14px', cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(22,163,74,0.3)',
  },
  notifyInfo: {
    display: 'flex', alignItems: 'center', gap: '12px',
    backgroundColor: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.2)',
    borderRadius: '10px', padding: '14px 16px',
  },
  confirmCard: {
    backgroundColor: '#242424', borderRadius: '14px',
    border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden',
  },
  confirmSection: {
    padding: '16px', display: 'flex', flexDirection: 'column', gap: '9px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  confirmSectionTitle: { fontSize: '12px', fontWeight: '700', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em' },
  confirmRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'rgba(255,255,255,0.8)', gap: '12px' },
  confirmTotal: {
    display: 'flex', justifyContent: 'space-between', fontSize: '19px',
    fontWeight: '900', color: '#fff', marginTop: '8px', paddingTop: '12px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  // ── Success screen ──
  successScreen: {
    padding: '36px 28px', display: 'flex', flexDirection: 'column',
    alignItems: 'center', textAlign: 'center', gap: '14px',
  },
  successAnim: { fontSize: '64px', lineHeight: 1 },
  successTitle: { fontSize: '28px', fontWeight: '900', color: '#fff', margin: 0 },
  orderId: { fontSize: '13px', color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', margin: 0 },
  successMsg: { fontSize: '15px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.65, maxWidth: '420px', margin: 0 },
  summaryBox: {
    backgroundColor: '#242424', borderRadius: '12px', padding: '16px',
    width: '100%', display: 'flex', flexDirection: 'column', gap: '10px',
    border: '1px solid rgba(204,0,0,0.2)',
  },
  summaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'rgba(255,255,255,0.75)' },
  waNotifyBox: {
    width: '100%', backgroundColor: 'rgba(37,211,102,0.08)',
    border: '1px solid rgba(37,211,102,0.25)', borderRadius: '12px',
    padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px',
  },
  waNotifyTitle: { fontSize: '15px', fontWeight: '800', color: '#4ade80' },
  waNumbers: { display: 'flex', flexDirection: 'column', gap: '4px' },
  waNum: { fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' },
  waHint: { fontSize: '12px', color: 'rgba(255,255,255,0.4)', lineHeight: 1.5, margin: 0 },
  manualBtns: { display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' },
  waBtn: (color) => ({
    padding: '11px 16px', borderRadius: '10px', border: 'none',
    backgroundColor: `${color}20`, color: '#4ade80',
    fontWeight: '700', fontSize: '13px', cursor: 'pointer',
    border: `1px solid ${color}40`,
  }),
  doneBtn: {
    padding: '14px 48px', borderRadius: '12px', border: 'none',
    background: 'linear-gradient(135deg,#CC0000,#990000)',
    color: '#fff', fontWeight: '800', fontSize: '16px', cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(204,0,0,0.35)', marginTop: '4px',
  },
};

export default CheckoutModal;
